import React, { useCallback, useEffect, useState } from 'react';
import { PlusIcon, MagnifyingGlassIcon, ExclamationTriangleIcon, XMarkIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import surgeonService from '../../services/surgeonService';
import { errText } from '../../services/scheduleService';
import { isAdmin } from '../../utils/permissions';

const EMPTY = {
  title: 'Dr', first_names: '', surname: '', initials: '', speciality: '', practice_number: '', hpcsa_number: '',
  rooms_name: '', rooms_phone: '', rooms_email: '', rooms_contact: '', notes: '',
};
const inputCls = 'w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-teal/40';
const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fullName = (s) => [s.title, s.initials, s.surname].filter(Boolean).join(' ');

const Field = ({ label, hint, className = '', children }) => (
  <label className={`block ${className}`}>
    <span className="mb-1 block text-xs font-semibold text-slate-600">{label}{hint && <span className="ml-1 font-normal text-slate-400">{hint}</span>}</span>
    {children}
  </label>
);

/**
 * Surgeons — the surgeons the practice works with. Their BHF practice number is
 * the referring provider on an anaesthetic claim, and their rooms are who the
 * practice arranges lists with. Surgeons are chosen on bookings (Theatre
 * Schedule) and carried onto invoices.
 */
const Surgeons = () => {
  const admin = isAdmin();
  const [q, setQ] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);
  const [inactive, setInactive] = useState(false);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // surgeon | {} for new

  const load = useCallback(async () => {
    try {
      setRows(await surgeonService.list(q.trim(), { inactive, missingNumber: missingOnly }));
      setError('');
    } catch (e) {
      setError(errText(e));
    }
  }, [q, inactive, missingOnly]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const missing = (rows || []).filter((s) => s.is_active && !s.practice_number).length;

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-deep">Surgeons</h1>
          <p className="mt-1 text-slate-500">The surgeons whose lists your anaesthetists cover. Their practice number goes on each claim as the referring provider.</p>
        </div>
        <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95">
          <PlusIcon className="h-5 w-5" /> Add surgeon
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {!missingOnly && missing > 0 && (
        <button onClick={() => setMissingOnly(true)} className="mb-4 flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-left text-sm text-amber-900 hover:bg-amber-100">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          <span><b>{missing} surgeon{missing === 1 ? '' : 's'}</b> without a practice number — invoices for their cases can’t be submitted until it’s added. Show them.</span>
        </button>
      )}

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-4 border-b border-slate-100 px-4 py-3">
          <div className="relative min-w-[16rem] flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, speciality, rooms or practice number…" className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} className="rounded border-slate-300" /> Missing practice number</label>
          <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} className="rounded border-slate-300" /> Show inactive</label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 font-semibold">Surgeon</th>
                <th className="px-2 py-2 font-semibold">Practice no.</th>
                <th className="px-2 py-2 font-semibold">Rooms</th>
                <th className="px-2 py-2 text-right font-semibold">Lists (90 days)</th>
                <th className="px-4 py-2 text-right font-semibold">Last list</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(rows || []).map((s) => (
                <tr key={s.surgeon_id} onClick={() => setEditing(s)} className={`cursor-pointer align-top hover:bg-slate-50 ${s.is_active ? '' : 'opacity-50'}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-slate-800">{fullName(s)}{!s.is_active && <span className="ml-2 rounded bg-slate-200 px-1.5 text-[10px] text-slate-600">inactive</span>}</div>
                    <div className="text-xs text-slate-500">{s.speciality || '—'}</div>
                  </td>
                  <td className="px-2 py-2.5">
                    {s.practice_number
                      ? <span className="font-mono text-slate-700">{s.practice_number}</span>
                      : <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800"><ExclamationTriangleIcon className="h-3.5 w-3.5" />Missing</span>}
                    {s.hpcsa_number && <div className="font-mono text-[11px] text-slate-400">{s.hpcsa_number}</div>}
                  </td>
                  <td className="px-2 py-2.5 text-xs text-slate-600">
                    <div className="font-semibold text-slate-700">{s.rooms_name || '—'}</div>
                    {s.rooms_contact && <div>{s.rooms_contact}</div>}
                    <div className="flex flex-wrap gap-x-3">
                      {s.rooms_phone && <span className="inline-flex items-center gap-1"><PhoneIcon className="h-3 w-3" />{s.rooms_phone}</span>}
                      {s.rooms_email && <span className="inline-flex items-center gap-1"><EnvelopeIcon className="h-3 w-3" />{s.rooms_email}</span>}
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-right tabular-nums text-slate-600">{s.recent_bookings}</td>
                  <td className="px-4 py-2.5 text-right text-xs text-slate-500">{fmtDate(s.last_booking)}</td>
                </tr>
              ))}
              {rows && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No surgeons{q || missingOnly ? ' match' : ' yet — add the surgeons you work with'}.</td></tr>}
              {rows === null && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {editing && <SurgeonEditor surgeon={editing} admin={admin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};

const SurgeonEditor = ({ surgeon, admin, onClose, onSaved }) => {
  const isNew = !surgeon.surgeon_id;
  const [form, setForm] = useState(() => Object.fromEntries(Object.keys(EMPTY).map((k) => [k, surgeon[k] ?? EMPTY[k]])));
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [matches, setMatches] = useState(null);
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setMatches(null); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = async (e, confirmDuplicate = false) => {
    e?.preventDefault();
    setBusy(true);
    setErrors([]);
    try {
      if (isNew) await surgeonService.create({ ...form, confirm_duplicate: confirmDuplicate });
      else await surgeonService.update(surgeon.surgeon_id, form);
      onSaved();
    } catch (ex) {
      if (ex.response?.data?.code === 'possible_duplicate') setMatches(ex.response.data.matches);
      else setErrors(ex.response?.data?.errors || [errText(ex)]);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await surgeonService.setActive(surgeon.surgeon_id, !surgeon.is_active);
      onSaved();
    } catch (ex) {
      setErrors([errText(ex)]);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <form onSubmit={save} className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-lg font-bold text-brand-deep">{isNew ? 'Add surgeon' : fullName(surgeon)}</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-6 gap-3 overflow-y-auto px-5 py-4">
          <Field label="Title" className="col-span-2 sm:col-span-1">
            <select className={inputCls} value={form.title || 'Dr'} onChange={set('title')}>{['Dr', 'Prof', 'Mr', 'Mrs', 'Ms'].map((t) => <option key={t}>{t}</option>)}</select>
          </Field>
          <Field label="First names" className="col-span-4 sm:col-span-2"><input className={inputCls} value={form.first_names} onChange={set('first_names')} /></Field>
          <Field label="Surname *" className="col-span-4 sm:col-span-2"><input className={inputCls} value={form.surname} onChange={set('surname')} required /></Field>
          <Field label="Initials" className="col-span-2 sm:col-span-1"><input className={inputCls} value={form.initials} onChange={set('initials')} maxLength={10} /></Field>
          <Field label="Speciality" className="col-span-6 sm:col-span-2"><input className={inputCls} value={form.speciality} onChange={set('speciality')} placeholder="Orthopaedic surgeon" /></Field>
          <Field label="BHF practice number" hint="7 digits — needed on claims" className="col-span-3 sm:col-span-2">
            <input className={`${inputCls} font-mono`} value={form.practice_number} onChange={set('practice_number')} maxLength={9} />
          </Field>
          <Field label="HPCSA number" hint="e.g. MP0123456" className="col-span-3 sm:col-span-2"><input className={`${inputCls} font-mono`} value={form.hpcsa_number} onChange={set('hpcsa_number')} maxLength={12} /></Field>

          <div className="col-span-6 mt-1 border-t border-slate-100 pt-3 text-xs font-bold uppercase tracking-wide text-slate-400">Rooms</div>
          <Field label="Rooms / practice name" className="col-span-6 sm:col-span-3"><input className={inputCls} value={form.rooms_name} onChange={set('rooms_name')} /></Field>
          <Field label="Contact person" hint="receptionist or PA" className="col-span-6 sm:col-span-3"><input className={inputCls} value={form.rooms_contact} onChange={set('rooms_contact')} /></Field>
          <Field label="Phone" className="col-span-6 sm:col-span-3"><input className={inputCls} value={form.rooms_phone} onChange={set('rooms_phone')} /></Field>
          <Field label="Email" className="col-span-6 sm:col-span-3"><input type="email" className={inputCls} value={form.rooms_email} onChange={set('rooms_email')} /></Field>
          <Field label="Notes" className="col-span-6"><textarea rows={2} className={`${inputCls} resize-none`} value={form.notes} onChange={set('notes')} /></Field>

          {matches && (
            <div className="col-span-6 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
              <p className="font-semibold">Already on the list:</p>
              <ul className="mt-1 list-disc pl-5 text-xs">{matches.map((m) => <li key={m.surgeon_id}>{fullName(m)}{m.speciality ? ` · ${m.speciality}` : ''}{m.practice_number ? ` · ${m.practice_number}` : ''}</li>)}</ul>
              <p className="mt-1 text-xs">If this is a different surgeon, add them anyway.</p>
            </div>
          )}
          {errors.length > 0 && <div className="col-span-6 space-y-0.5 text-sm text-red-600">{errors.map((er) => <p key={er}>{er}</p>)}</div>}
        </div>
        <div className="flex items-center gap-2 border-t border-slate-100 px-5 py-3">
          {!isNew && admin && (
            <button type="button" onClick={toggleActive} disabled={busy} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100">
              {surgeon.is_active ? 'Deactivate' : 'Reactivate'}
            </button>
          )}
          <div className="flex-1" />
          <button type="button" onClick={onClose} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          {matches ? (
            <button type="button" onClick={() => save(null, true)} disabled={busy} className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">Add anyway</button>
          ) : (
            <button type="submit" disabled={busy} className="rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-60">{busy ? 'Saving…' : isNew ? 'Add surgeon' : 'Save'}</button>
          )}
        </div>
      </form>
    </div>
  );
};

export default Surgeons;
